import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { ACTIVITY_ACTIONS } from '@/shared/constants';
import { committed } from '@/shared/data';

function fromEntry(a) {
  return {
    Name: a?.Name ?? '',
    action_c: a?.action_c || 'Order',
    target_c: a?.target_c ?? '',
    detail_c: a?.detail_c ?? '',
  };
}

/** Add a note to the audit trail by hand, or correct an entry. */
export default function ActivityDialog({ open, onOpenChange, entry, onSaved }) {
  const editing = !!entry;
  const [form, setForm] = useState(fromEntry(entry));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromEntry(entry));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  async function submit() {
    if (!form.Name.trim()) {
      toast.error('Say what happened');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      action_c: form.action_c,
      target_c: form.target_c.trim(),
      detail_c: form.detail_c.trim(),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('activity_c').update({ Id: entry.Id, ...fields })
        : await sdk.table('activity_c').create(fields);
      const saved = committed(res, editing ? 'Entry updated' : 'Entry added');
      if (!saved) return;
      onOpenChange(false);
      setForm(fromEntry(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit entry' : 'Add an entry'}
      description={editing ? undefined : 'For something done outside the app, like a refund sent by hand.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Add entry'}
      busyLabel={editing ? 'Saving…' : 'Adding…'}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What happened" htmlFor="ac-name">
          <Input id="ac-name" value={form.Name} onChange={text('Name')} placeholder="Refund sent" />
        </Field>
        <Field label="Area" htmlFor="ac-action">
          <PickSelect id="ac-action" value={form.action_c} onChange={set('action_c')} values={ACTIVITY_ACTIONS} placeholder="Choose an area" />
        </Field>
      </div>
      <Field label="Applies to" htmlFor="ac-target" hint="An order code, customer email, discount code or team member.">
        <Input id="ac-target" value={form.target_c} onChange={text('target_c')} placeholder="GC-4KX9QZ" />
      </Field>
      <Field label="Detail" htmlFor="ac-detail">
        <Textarea id="ac-detail" rows={3} value={form.detail_c} onChange={text('detail_c')} placeholder="$28 back to PayPal after a double charge" />
      </Field>
    </FormDialog>
  );
}
